import BlockRounded from "@mui/icons-material/BlockRounded";
import { Box, Typography } from "@mui/material";
import { DateTime } from "luxon";
import type { FC, ReactNode } from "react";
import { useTranslation } from "react-i18next";
import MetaChip from "../../../../components/UIToolkit/Badges/MetaChip";
import { ValueChoiceGrid } from "../../../../components/UIToolkit/Clickable/ValueChoiceGrid";
import { EyebrowLabel } from "../../../../components/UIToolkit/molecules/EyebrowLabel";
import SystemBanner from "../../../../components/UIToolkit/molecules/SystemBanner";
import { useLastNameFirst } from "../../../../hooks/useNameFormat";
import { FONT_SIZE, INK, NEUTRAL, RADIUS, SURFACE } from "../../../../theme/tokens";
import { formatPersonFullName } from "../../../../utils/formatters";
import type { MergeNurseSummary, NurseMergePreview } from "../../../../utils/nurseMergeModels";
import { formatNurseLabel, type MergeChoices, removedOverlapShiftCount } from "./mergeLogic";
import type { MergeLookups } from "./useMergeDuplicateFlow";

type Props = {
	preview: NurseMergePreview;
	choices: MergeChoices;
	onChoicesChange: (next: MergeChoices) => void;
	lookups: MergeLookups;
	/** The department's timezone — overlapping shifts are shown in local time. */
	timezone: string;
};

/** Step 3, only when needed: what blocks the merge, and the choices only a person can make. */
export const MergeDecideStep: FC<Props> = ({ preview, choices, onChoicesChange, lookups, timezone }) => {
	const { t } = useTranslation();
	const lastNameFirst = useLastNameFirst();
	const summaryById = new Map([preview.primary, preview.duplicate].map((summary) => [summary.nurseId, summary]));

	return (
		<Box sx={{ display: "flex", flexDirection: "column", gap: 3 }} data-testid="merge-employees-decide-step">
			{preview.blockers.length > 0 && <Blockers preview={preview} />}
			{preview.departmentConflicts.length > 0 && (
				<Section title={t("nurse.merge.decide.departments.title")} description={t("nurse.merge.decide.departments.description")}>
					{preview.departmentConflicts.map((conflict) => {
						const active = conflict.primaryActive ? preview.primary : preview.duplicate;
						const inactive = conflict.primaryActive ? preview.duplicate : preview.primary;
						const department = lookups.departmentName(conflict.departmentId);
						const chosen = choices.keepActiveByDepartment[conflict.departmentId];
						return (
							<ChoiceCard key={conflict.departmentId} dataTestId={`merge-employees-department-${conflict.departmentId}`}>
								<Box sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}>
									<Typography sx={{ fontSize: FONT_SIZE.EMPHASIS, fontWeight: 700, color: INK.PRIMARY }}>{department}</Typography>
									<Typography sx={{ fontSize: FONT_SIZE.CAPTION, color: INK.MUTED }}>
										{t("nurse.merge.decide.departments.state", {
											active: formatNurseLabel(active, lastNameFirst),
											inactive: formatNurseLabel(inactive, lastNameFirst),
										})}
									</Typography>
								</Box>
								<ValueChoiceGrid
									columns={2}
									selected={chosen === undefined ? null : chosen ? "active" : "inactive"}
									onSelect={(value) =>
										onChoicesChange({ ...choices, keepActiveByDepartment: { ...choices.keepActiveByDepartment, [conflict.departmentId]: value === "active" } })
									}
									items={[
										{
											value: "active",
											label: t("nurse.merge.decide.keep.active"),
											caption: t("nurse.merge.decide.keep.as", { id: active.nurseId }),
											ariaLabel: t("nurse.merge.decide.keep.active.aria", { department }),
											dataTestId: `merge-employees-department-${conflict.departmentId}-active`,
										},
										{
											value: "inactive",
											label: t("nurse.merge.decide.keep.inactive"),
											caption: t("nurse.merge.decide.keep.as", { id: inactive.nurseId }),
											ariaLabel: t("nurse.merge.decide.keep.inactive.aria", { department }),
											dataTestId: `merge-employees-department-${conflict.departmentId}-inactive`,
										},
									]}
								/>
							</ChoiceCard>
						);
					})}
				</Section>
			)}
			{preview.recurringConflicts.length > 0 && (
				<Section title={t("nurse.merge.decide.recurring.title")} description={t("nurse.merge.decide.recurring.description")}>
					{preview.recurringConflicts.map((conflict) => {
						const department = lookups.departmentName(conflict.departmentId);
						const selected = choices.recurringKeepNurseIdByDepartment[conflict.departmentId];
						const selectedSummary = selected === undefined ? undefined : requireSummary(summaryById, selected, conflict.departmentId);
						return (
							<ChoiceCard key={conflict.departmentId} dataTestId={`merge-employees-recurring-${conflict.departmentId}`}>
								<Typography sx={{ fontSize: FONT_SIZE.EMPHASIS, fontWeight: 700, color: INK.PRIMARY }}>{department}</Typography>
								<ValueChoiceGrid
									columns={{ xs: 1, sm: 2 }}
									selected={selected === undefined ? null : selected}
									onSelect={(value) =>
										onChoicesChange({
											...choices,
											recurringKeepNurseIdByDepartment: {
												...choices.recurringKeepNurseIdByDepartment,
												[conflict.departmentId]: Number(value),
											},
										})
									}
									items={[
										{ summary: preview.primary, count: conflict.primaryActiveCount },
										{ summary: preview.duplicate, count: conflict.duplicateActiveCount },
									].map(({ summary, count }) => ({
										value: summary.nurseId,
										label: formatPersonFullName(summary, lastNameFirst),
										caption: t("nurse.merge.decide.recurring.template.count", { id: summary.nurseId, count }),
										ariaLabel: t("nurse.merge.decide.recurring.choice.aria", {
											department,
											name: formatNurseLabel(summary, lastNameFirst),
											count,
										}),
										dataTestId: `merge-employees-recurring-${conflict.departmentId}-keep-${summary.nurseId}`,
									}))}
								/>
								<Typography sx={{ fontSize: FONT_SIZE.SECONDARY, color: INK.SECONDARY, lineHeight: 1.5 }}>
									{selectedSummary === undefined
										? t("nurse.merge.decide.recurring.pending")
										: t("nurse.merge.decide.recurring.selected", {
												kept: formatNurseLabel(selectedSummary, lastNameFirst),
												stopped: formatNurseLabel(selectedSummary.nurseId === preview.primary.nurseId ? preview.duplicate : preview.primary, lastNameFirst),
											})}
								</Typography>
							</ChoiceCard>
						);
					})}
				</Section>
			)}
			{preview.shiftOverlaps.length > 0 && (
				<Section
					title={t("nurse.merge.decide.overlaps.title", { count: preview.shiftOverlaps.length })}
					description={t("nurse.merge.decide.overlaps.description")}
				>
					<ChoiceCard dataTestId="merge-employees-overlaps">
						<Box component="ul" sx={{ m: 0, p: 0, listStyle: "none", display: "flex", flexDirection: "column", gap: 0.75 }}>
							{preview.shiftOverlaps.map((overlap) => (
								<Box
									component="li"
									key={`${overlap.primaryShiftId}-${overlap.duplicateShiftId}`}
									sx={{ display: "flex", flexDirection: "column", gap: 0.5, fontSize: FONT_SIZE.EMPHASIS, color: INK.PRIMARY }}
								>
									<MetaChip
										label={t(overlap.isActual ? "nurse.merge.decide.overlaps.actual" : "nurse.merge.decide.overlaps.scheduled")}
										tone={overlap.isActual ? "info" : "neutral"}
									/>
									{[
										{ summary: preview.primary, start: overlap.primaryStartTime, end: overlap.primaryEndTime },
										{ summary: preview.duplicate, start: overlap.duplicateStartTime, end: overlap.duplicateEndTime },
									].map(({ summary, start, end }) => (
										<Typography
											key={summary.nurseId}
											sx={{ fontSize: FONT_SIZE.EMPHASIS, color: INK.PRIMARY, fontWeight: 600, fontVariantNumeric: "tabular-nums" }}
											data-testid={`merge-employees-overlap-window-${summary.nurseId}`}
										>
											{t("nurse.merge.decide.overlaps.window", {
												name: formatNurseLabel(summary, lastNameFirst),
												window: formatShiftWindow(start, end, timezone),
											})}
										</Typography>
									))}
								</Box>
							))}
						</Box>
						<ValueChoiceGrid
							columns={{ xs: 1, sm: 2 }}
							selected={choices.overlapKeepNurseId}
							onSelect={(value) => onChoicesChange({ ...choices, overlapKeepNurseId: Number(value) })}
							items={[preview.primary, preview.duplicate].map((summary) => ({
								value: summary.nurseId,
								label: t("nurse.merge.decide.overlaps.keep", { name: formatPersonFullName(summary, lastNameFirst) }),
								caption: `#${summary.nurseId}`,
								dataTestId: `merge-employees-overlap-keep-${summary.nurseId}`,
							}))}
						/>
						<Typography sx={{ fontSize: FONT_SIZE.SECONDARY, color: INK.SECONDARY, lineHeight: 1.5 }} data-testid="merge-employees-overlap-consequence">
							{overlapConsequence(t, preview, choices.overlapKeepNurseId, summaryById, lastNameFirst)}
						</Typography>
					</ChoiceCard>
				</Section>
			)}
		</Box>
	);
};

const requireSummary = (summaryById: Map<number, MergeNurseSummary>, nurseId: number, departmentId: string): MergeNurseSummary => {
	const summary = summaryById.get(nurseId);
	if (summary === undefined) throw new Error(`Invalid recurring schedule choice for ${departmentId}`);
	return summary;
};

const overlapConsequence = (
	t: ReturnType<typeof useTranslation>["t"],
	preview: NurseMergePreview,
	keepNurseId: number | null,
	summaryById: Map<number, MergeNurseSummary>,
	lastNameFirst: boolean,
): string => {
	const kept = keepNurseId === null ? undefined : summaryById.get(keepNurseId);
	if (!kept) return t("nurse.merge.decide.overlaps.pending");
	const losing = kept.nurseId === preview.primary.nurseId ? preview.duplicate : preview.primary;
	return t("nurse.merge.decide.overlaps.consequence", {
		count: removedOverlapShiftCount(preview, kept.nurseId),
		kept: formatNurseLabel(kept, lastNameFirst),
		removed: formatNurseLabel(losing, lastNameFirst),
	});
};

/** "Mon, Sep 29 · 7:00 AM – 3:00 PM" in the department's timezone; the end date is named only when it differs. */
const formatShiftWindow = (start: DateTime, end: DateTime, timezone: string): string => {
	const localStart = start.setZone(timezone);
	const localEnd = end.setZone(timezone);
	const day = localStart.toLocaleString({ weekday: "short", month: "short", day: "numeric" });
	const endText = localEnd.hasSame(localStart, "day")
		? localEnd.toLocaleString(DateTime.TIME_SIMPLE)
		: `${localEnd.toLocaleString({ weekday: "short", month: "short", day: "numeric" })} ${localEnd.toLocaleString(DateTime.TIME_SIMPLE)}`;
	return `${day} · ${localStart.toLocaleString(DateTime.TIME_SIMPLE)} – ${endText}`;
};

const Blockers: FC<{ preview: NurseMergePreview }> = ({ preview }) => {
	const { t } = useTranslation();
	const unhandledRows = preview.unhandledReferences.reduce((sum, reference) => sum + reference.rows, 0);
	return (
		<SystemBanner
			tone="error"
			icon={<BlockRounded />}
			title={t("nurse.merge.blocked.title")}
			descriptionComponent="div"
			dataTestId="merge-employees-blockers"
			description={
				<Box component="ul" sx={{ m: 0, pl: 2.5, display: "flex", flexDirection: "column", gap: 0.75 }}>
					{preview.blockers.map((blocker) => (
						<li key={blocker} data-testid={`merge-employees-blocker-${blocker}`}>
							{t(`nurse.merge.blocker.${blocker}`, { count: unhandledRows })}
						</li>
					))}
				</Box>
			}
		/>
	);
};

const Section: FC<{ title: string; description: string; children: ReactNode }> = ({ title, description, children }) => (
	<Box component="section" sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
		<Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
			<EyebrowLabel component="h3">{title}</EyebrowLabel>
			<Typography sx={{ fontSize: FONT_SIZE.SECONDARY, color: INK.SECONDARY, lineHeight: 1.5 }}>{description}</Typography>
		</Box>
		{children}
	</Box>
);

const ChoiceCard: FC<{ children: ReactNode; dataTestId: string }> = ({ children, dataTestId }) => (
	<Box
		data-testid={dataTestId}
		sx={{
			display: "flex",
			flexDirection: "column",
			gap: 1.5,
			p: 1.5,
			borderRadius: RADIUS.CONTROL / RADIUS.CARD,
			border: `1px solid ${NEUTRAL[200]}`,
			backgroundColor: SURFACE,
		}}
	>
		{children}
	</Box>
);
